import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../../shared/api/errors';
import { relativeTime } from '../../shared/lib/format';
import { ClippedText } from '../../shared/ui/clipped-text';
import { EventsIcon } from '../../shared/ui/icons';
import { Page, PageHeader } from '../../shared/ui/layout';
import { ListFooter } from '../../shared/ui/list-footer';
import { MenuItem } from '../../shared/ui/menu';
import { Note } from '../../shared/ui/note';
import { CopyMenuItem, RowMenu } from '../../shared/ui/row-menu';
import { StatusBadge } from '../../shared/ui/status-badge';
import { Table, TruncateMiddle, type IColumn } from '../../shared/ui/table';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../../shared/ui/states';
import {
  attentionReasons,
  counted,
  keepsSource,
  listSourcesPage,
  type ISourceFilters,
  type TSource,
} from '../../entities/source';
import { flowKeys, listFlows } from '../../entities/flow';
import { authIssueBySource, listOpenAuthIssues, type TIssue } from '../../entities/issue';
import { EMPTY_RUN_FILTERS, runFiltersToSearch } from '../../entities/run';
import { SourceCreate } from '../../features/source-create';
import { SourceFiltersBar } from './ui/filters-bar';

const FLOW_PAGE = 200;

const Checked = ({ source }: { source: TSource }) => {
  if (source.unauthenticated) {
    return (
      <span
        className="text-danger-text"
        title="Nothing is checked: anything reaching the address is accepted"
      >
        no check
      </span>
    );
  }
  const { checked, passed } = source.verification;
  if (checked === null || passed === null || checked === 0) {
    return <span className="text-faint">nothing yet</span>;
  }
  return (
    <span className={passed < checked ? 'text-danger-text' : undefined}>
      {`${passed.toLocaleString('en-US')} of ${checked.toLocaleString('en-US')}`}
    </span>
  );
};

// Live flows that start from it; red when none takes a request.
const Takers = ({ source, flows }: { source: TSource; flows: number | undefined }) => {
  if (flows === undefined) {
    return (
      <span className="text-faint" title="Not counted: more flows than one page">
        —
      </span>
    );
  }
  if (source.state === 'active' && flows === 0) {
    return (
      <span
        className="text-danger-text"
        title="Nothing takes its requests: they are kept and go nowhere"
      >
        none
      </span>
    );
  }
  return <span>{flows.toLocaleString('en-US')}</span>;
};

interface IIssueColumnState {
  authIssues: Map<string, TIssue>;
  pending: boolean;
  failed: boolean;
}

const IssueCell = ({ source, issues }: { source: TSource; issues: IIssueColumnState }) => {
  if (issues.pending) {
    return (
      <span
        aria-hidden
        className="inline-block h-3 w-8 animate-[hp-pulse_1.8s_ease-in-out_infinite] rounded-control bg-surface-raised"
      />
    );
  }
  if (issues.failed) {
    return (
      <span className="text-faint" title="Could not check for an open verification issue">
        ?
      </span>
    );
  }
  const issue = issues.authIssues.get(source.id);
  return issue ? (
    <Link
      to="/issues/$issueId"
      params={{ issueId: issue.id }}
      className="text-danger-text hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      open
    </Link>
  ) : (
    <span className="text-faint">—</span>
  );
};

// The Flows view of Events: what this source started, over the window its counts cover.
const eventsSearch = (source: TSource) =>
  runFiltersToSearch({
    ...EMPTY_RUN_FILTERS,
    minutes: (source.activity?.windowHours ?? 24) * 60,
    sourceId: [source.id],
  });

const columns = (
  windowHours: number | null,
  issues: IIssueColumnState,
  flowCounts: ReadonlyMap<string, number> | null,
): IColumn<TSource>[] => [
  {
    key: 'state',
    header: 'Status',
    width: 'w-col-status',
    ui: true,
    render: (source) => <StatusBadge vocabulary="source" value={source.state} />,
  },
  {
    key: 'name',
    header: 'Source',
    width: 'w-col-source',
    ui: true,
    render: (source) => <ClippedText text={source.name} lines={2} />,
  },
  {
    key: 'url',
    header: 'Address',
    width: 'grow',
    render: (source) => <TruncateMiddle text={source.ingestUrl} tail={22} />,
  },
  {
    key: 'verified',
    header: 'Verified',
    width: 'w-col-time',
    align: 'right',
    hint: 'Requests whose signature passed, of those checked, over the same window as Requests.',
    render: (source) => <Checked source={source} />,
  },
  {
    key: 'issue',
    header: 'Issue',
    width: 'w-col-try',
    ui: true,
    render: (source) => <IssueCell source={source} issues={issues} />,
  },
  {
    key: 'flows',
    header: 'Flows',
    width: 'w-col-time',
    align: 'right',
    hint: 'Live flows that start from it. None on a receiving source means requests are kept and go nowhere.',
    render: (source) => (
      <Takers source={source} flows={flowCounts ? (flowCounts.get(source.id) ?? 0) : undefined} />
    ),
  },
  {
    key: 'last',
    header: 'Last request',
    width: 'w-col-when',
    align: 'right',
    render: (source) =>
      source.activity?.lastRequestAt ? (
        relativeTime(source.activity.lastRequestAt, Date.now())
      ) : (
        <span className="text-faint">never</span>
      ),
  },
  {
    key: 'day',
    header: 'Requests',
    width: 'w-col-when',
    align: 'right',
    hint: windowHours
      ? `Counts the past ${windowHours} hours and is recomputed every few minutes; a dash means it has not been counted yet, which is not the same as none.`
      : 'Recomputed every few minutes; a dash means it has not been counted yet, which is not the same as none.',
    // A dash, not a zero: `computedAt: null` is "the rollup has not run".
    render: (source) =>
      counted(source.activity?.requestsInWindow ?? 0, source.activity?.computedAt ?? null) ?? (
        <span className="text-faint" title="Not counted yet">
          —
        </span>
      ),
  },
  {
    key: 'menu',
    header: '',
    width: 'w-col-row-menu',
    ui: true,
    render: (source) => (
      <RowMenu label={source.name}>
        <CopyMenuItem value={source.ingestUrl}>Copy address</CopyMenuItem>
        <MenuItem asChild>
          <Link to="/events" search={eventsSearch(source)}>
            <EventsIcon />
            See its events
          </Link>
        </MenuItem>
      </RowMenu>
    ),
  },
];

interface ISourcesPageProps {
  filters: ISourceFilters;
  onFilters: (next: ISourceFilters) => void;
  onOpen: (source: TSource) => void;
}

const noFiltersActive = (filters: ISourceFilters): boolean =>
  !filters.state && !filters.auth && !filters.hasProblems && !filters.q;

export const SourcesPage = ({ filters, onFilters, onOpen }: ISourcesPageProps) => {
  const sources = useQuery({
    queryKey: ['sources', 'list', filters.q ?? null, filters.cursor ?? null],
    queryFn: () => listSourcesPage({ q: filters.q, cursor: filters.cursor }),
    placeholderData: keepPreviousData,
  });

  const all = useMemo(() => sources.data?.data ?? [], [sources.data]);
  // Exactly the ids on the page, not a global `limit`: keeps a badge from disappearing
  // once there are more than 200 open auth issues across the organization.
  const sourceIds = useMemo(() => all.map((source) => source.id), [all]);
  const issues = useQuery({
    queryKey: ['issues', 'open-auth', sourceIds],
    queryFn: () => listOpenAuthIssues(sourceIds),
    enabled: sourceIds.length > 0,
  });

  const authIssues = useMemo(() => authIssueBySource(issues.data?.data ?? []), [issues.data]);
  const issuePending = issues.isPending && sourceIds.length > 0;
  const issueColumn: IIssueColumnState = useMemo(
    () => ({ authIssues, pending: issuePending, failed: issues.isError }),
    [authIssues, issuePending, issues.isError],
  );
  // One page of flows, counted per source; past one page the count is not claimed at all.
  const flows = useQuery({
    queryKey: flowKeys.list({ limit: FLOW_PAGE }),
    queryFn: () => listFlows({ limit: FLOW_PAGE }),
  });
  const flowCounts = useMemo(() => {
    if (!flows.data || flows.data.nextCursor) return null;
    const counts = new Map<string, number>();
    for (const flow of flows.data.data) {
      const sourceId = flow.trigger.sourceId;
      if (!sourceId || !flow.liveVersion || flow.state === 'disabled') continue;
      counts.set(sourceId, (counts.get(sourceId) ?? 0) + 1);
    }
    return counts;
  }, [flows.data]);

  const hasProblems = (source: TSource) =>
    attentionReasons(
      source,
      authIssues.has(source.id),
      flowCounts ? (flowCounts.get(source.id) ?? 0) : undefined,
    ).length > 0;
  const rows = all.filter((source) => keepsSource(source, filters, hasProblems));
  const windowHours = all[0]?.activity?.windowHours ?? null;
  const tableColumns = useMemo(
    () => columns(windowHours, issueColumn, flowCounts),
    [windowHours, issueColumn, flowCounts],
  );

  // "Has problems" reads the joined auth-issue map: until it settles, its count and rows would
  // understate what is broken, so both are held back rather than shown wrong.
  const issuesSettled = issues.isSuccess || sourceIds.length === 0;
  const problemsUnready = Boolean(filters.hasProblems) && !issuesSettled;
  // A count from one page of several would understate; only a whole list is counted.
  const wholeList = !filters.cursor && !sources.data?.nextCursor;
  const hasProblemsCount =
    sources.isSuccess && wholeList && issuesSettled
      ? all.filter((source) => keepsSource(source, { ...filters, hasProblems: true }, hasProblems))
          .length
      : undefined;

  const isForbidden =
    sources.isError && sources.error instanceof ApiError && sources.error.status === 403;
  const nothingExists = sources.isSuccess && all.length === 0 && noFiltersActive(filters);
  const noMatch = sources.isSuccess && !nothingExists && !problemsUnready && rows.length === 0;

  return (
    <>
      <PageHeader
        title="Sources"
        description="Where webhooks arrive, and how each sender is proved to be who it says."
        actions={<SourceCreate onCreated={onOpen} />}
      />

      {!nothingExists && (
        <SourceFiltersBar
          filters={filters}
          onFilters={onFilters}
          hasProblemsCount={hasProblemsCount}
        />
      )}

      <Page width="full" className="flex-1">
        <div className="flex flex-col gap-section-gap">
          {sources.isPending && <LoadingState rows={6} />}
          {sources.isError && isForbidden && <ForbiddenState />}
          {sources.isError && !isForbidden && (
            <ErrorState error={sources.error} onRetry={() => void sources.refetch()} />
          )}

          {nothingExists && (
            <EmptyState
              title="No sources yet"
              description="A source is an address a sender posts to. Nothing can arrive until there is one."
              action={<SourceCreate onCreated={onOpen} />}
            />
          )}

          {all.length > 0 && issues.isError && !filters.hasProblems && (
            <Note tone="danger">
              Could not check which sources have an open verification issue — the Issue column and
              the Has problems count may be incomplete.
            </Note>
          )}

          {problemsUnready && issues.isError && (
            <ErrorState error={issues.error} onRetry={() => void issues.refetch()} />
          )}
          {problemsUnready && !issues.isError && <LoadingState rows={6} />}

          {noMatch && <NoMatchState onReset={() => onFilters({})} />}

          {!problemsUnready && rows.length > 0 && (
            <>
              <Table
                tall
                layoutId="sources"
                columns={tableColumns}
                rows={rows}
                rowKey={(row) => row.id}
                muted={(row) => row.state !== 'active'}
                onRowClick={onOpen}
              />
              <ListFooter
                note={
                  windowHours
                    ? `Last ${windowHours} hours. Verified = signatures that passed, of those checked. Flows = live flows that start from it.`
                    : 'Verified = signatures that passed, of those checked. Flows = live flows that start from it.'
                }
                shown={rows.length}
                total={sources.data?.total}
                onNext={
                  sources.data?.nextCursor
                    ? () => onFilters({ ...filters, cursor: sources.data?.nextCursor ?? undefined })
                    : undefined
                }
              />
            </>
          )}
        </div>
      </Page>
    </>
  );
};
